import { Play } from '@tunasong/icons'
import { INTERVAL_POOL } from '@tunasong/music-lib'
import type { IntervalKind, IntervalQuestion } from '@tunasong/music-lib'
import { Button } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { EarTrainingAnswerButton } from './ear-training-answer-button.js'
import type { AnswerButtonState } from './ear-training-answer-button.js'
import styles from './interval-question.module.css'

export interface IntervalQuestionPanelProps {
  question: IntervalQuestion
  scoreCorrect: number
  scoreAnswered: number
  /**
   * When false, answer buttons render dimmed (post-answer feedback phase).
   * The Hear-it button stays interactive in both modes.
   */
  interactive: boolean
  onHearIt: () => void
  onAnswer: (picked: IntervalKind) => void
}

/**
 * Ear Training Interval ID question panel. Renders a generic "What interval
 * did you hear?" prompt + Hear-it audition button + 12-button answer grid
 * (P1..M7 from `INTERVAL_POOL`). Stateless — the host owns session state.
 *
 * Unlike Function Quiz, the panel deliberately does NOT render the chord/
 * interval name in the header: the structural name IS the answer.
 *
 * Spec: docs/superpowers/specs/2026-05-20-ear-training-suite-design.md §4.3
 */
export const IntervalQuestionPanel: FC<IntervalQuestionPanelProps> = props => {
  const { scoreCorrect, scoreAnswered, interactive, onHearIt, onAnswer } = props
  const buttonState: AnswerButtonState = interactive ? 'idle' : 'dimmed'
  return (
    <section className={styles.question} aria-label="Ear training question">
      <header className={styles.header}>
        <span
          className={styles.scoreChip}
          aria-label={`${scoreCorrect} correct out of ${scoreAnswered} answered`}
        >
          {scoreCorrect} / {scoreAnswered}
        </span>
      </header>
      <p className={styles.prompt}>What interval did you hear?</p>
      <div className={styles.actionRow}>
        <Button variant="outlined" size="small" startIcon={<Play />} onClick={onHearIt}>
          Hear it
        </Button>
      </div>
      <div className={styles.cardGrid}>
        {INTERVAL_POOL.map(entry => (
          <EarTrainingAnswerButton
            key={entry.kind}
            pickValue={entry.kind}
            lexiconId={entry.lexiconId}
            fallbackLongName={entry.displayName}
            fallbackShortName={entry.displayNameShort}
            state={buttonState}
            onPick={picked => onAnswer(picked as IntervalKind)}
          />
        ))}
      </div>
    </section>
  )
}
